import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  AddFavoriteDocument,
  ArchiveIssueDocument,
  DeleteIssueDocument,
  type EntityType,
  GetMyPreferencesDocument,
  IssuesDocument,
  MoveIssueToStageDocument,
  MoveIssueToWorkflowDocument,
  RemoveFavoriteDocument,
  RenameIssueDocument,
  UnarchiveIssueDocument,
  UpdateIssueAssigneesDocument,
} from "#app/lib/data";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { UNDO_TOAST_SHORTCUT, UNDO_TOAST_TIMEOUT_MS } from "#app/lib/undo-shortcut";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";
import { removeIssueFromStageCache } from "./kanban-cache-utils";
import { useAnalytics, AnalyticsContext, KanbanEvent } from "#app/lib/analytics";
import { optimisticIssue } from "#app/lib/data/optimistic-issue";

/** Minimal issue shape needed by mutation handlers. */
type IssueRef = { id: string; issueId: string; name: string; stageId: string };

export function useIssueCardActions() {
  const { track } = useAnalytics();
  const [renameMutation] = useMutation(RenameIssueDocument);
  const [archiveMutation] = useMutation(ArchiveIssueDocument);
  const [unarchiveMutation] = useMutation(UnarchiveIssueDocument);
  const [deleteMutation] = useMutation(DeleteIssueDocument);
  const [moveStageMutation] = useMutation(MoveIssueToStageDocument);
  const [addFavoriteMutation] = useMutation(AddFavoriteDocument);
  const [removeFavoriteMutation] = useMutation(RemoveFavoriteDocument);
  const [moveToWorkflowMutation] = useMutation(MoveIssueToWorkflowDocument);
  const [updateAssigneesMutation] = useMutation(UpdateIssueAssigneesDocument);

  const variables = useMemo(() => ({}), []);
  const { data: prefsData } = useQuery(GetMyPreferencesDocument, {
    variables,
    fetchPolicy: "cache-first",
  });

  const favoriteIssueIds = useMemo(() => {
    const favorites = prefsData?.myPreferences?.favorites ?? [];
    return new Set(
      (favorites as { type: string; referenceId: string }[])
        .filter((f) => f.type === "issue")
        .map((f) => String(f.referenceId)),
    );
  }, [prefsData]);

  const isIssueFavorited = useCallback(
    (issueId: string) => favoriteIssueIds.has(issueId),
    [favoriteIssueIds],
  );

  const renameIssue = useCallback(
    async (issueId: string, name: string) => {
      await renameMutation({
        variables: { issueId, name },
        optimisticResponse: {
          updateIssue: optimisticIssue({ id: issueId, issueId, name }),
        },
      });
      track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Rename });
    },
    [renameMutation, track],
  );

  // Archive and unarchive leave the card where it was: the toast is the way
  // back, not a confirmation — the neutral undo toast (no `type`), with the
  // inverse mutation on its action and ⌘Z while it stands.
  const undoToast = useCallback(
    (title: string, undo: () => Promise<unknown>) => {
      const toastId = toastManager.add({
        title,
        actionProps: {
          children: "Undo",
          onClick: () => {
            void undo();
            // Toast.Action does not close its toast; an offer that was taken
            // must leave with the press.
            toastManager.close(toastId);
          },
        },
        data: { actionShortcut: UNDO_TOAST_SHORTCUT },
        timeout: UNDO_TOAST_TIMEOUT_MS,
      });
    },
    [],
  );

  const runArchive = useCallback(
    async (issue: IssueRef, via: "menu" | "undo_toast"): Promise<boolean> => {
      try {
        const result = await archiveMutation({
          variables: { issueId: issue.issueId },
          update(cache, { data }) {
            if (data?.archiveIssue.__typename !== "Issue") return;
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        // A refusal is a result, not a throw: no card leaves, and no Undo
        // is offered for an archive that did not happen.
        const payload = result.data?.archiveIssue;
        if (payload?.__typename !== "Issue") {
          throw new Error(resultMessage(payload) ?? "Archive failed");
        }
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Archive, data: { via } });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to archive Project");
        return false;
      }
    },
    [archiveMutation, track],
  );

  const runUnarchive = useCallback(
    async (issue: IssueRef, via: "menu" | "undo_toast"): Promise<boolean> => {
      try {
        // The archive took the card out of its column's cache, so the columns
        // refetch to bring it back. A board that also listens for the
        // `IssueUnarchived` event gets it sooner; a cross-workflow board
        // (a team or member board) does not listen, and needs this.
        const result = await unarchiveMutation({
          variables: { issueId: issue.issueId },
          refetchQueries: [IssuesDocument],
        });
        const payload = result.data?.unarchiveIssue;
        if (payload?.__typename !== "Issue") {
          throw new Error(resultMessage(payload) ?? "Unarchive failed");
        }
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Unarchive, data: { via } });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to unarchive Project");
        return false;
      }
    },
    [unarchiveMutation, track],
  );

  const archiveIssue = useCallback(
    async (issue: IssueRef) => {
      if (!(await runArchive(issue, "menu"))) return;
      undoToast("Project archived", () => runUnarchive(issue, "undo_toast"));
    },
    [runArchive, runUnarchive, undoToast],
  );

  const unarchiveIssue = useCallback(
    async (issue: IssueRef) => {
      if (!(await runUnarchive(issue, "menu"))) return;
      undoToast("Project unarchived", () => runArchive(issue, "undo_toast"));
    },
    [runArchive, runUnarchive, undoToast],
  );

  // Answers whether the delete landed, so a caller confirms (or follows up
  // on) only a delete that happened; a failure has already said so.
  const deleteIssue = useCallback(
    async (issue: IssueRef): Promise<boolean> => {
      try {
        await deleteMutation({
          variables: { issueId: issue.issueId },
          optimisticResponse: {
            deleteIssue: {
              __typename: "Issue",
              id: issue.id,
              issueId: issue.issueId,
              trashedAt: new Date().toISOString(),
            },
          },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
            // Drop the issue from EVERY cached `issues(...)` connection (the
            // map infobox's by-id query, drilldowns) BEFORE evicting: an
            // eviction alone only makes those reads incomplete, and Apollo
            // does not refetch a watched query over an incomplete read — the
            // stale edges would keep serving the deleted card until reload.
            removeFromConnection(cache, "issues", {
              __typename: "Issue",
              issueId: issue.issueId,
            });
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this issue.
            const cacheId = cache.identify({
              __typename: "Issue",
              issueId: issue.issueId,
            });
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.Delete });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to delete Project");
        return false;
      }
    },
    [deleteMutation, track],
  );

  const changeStage = useCallback(
    async (issue: IssueRef, toStageId: string) => {
      try {
        await moveStageMutation({
          variables: {
            issueId: issue.issueId,
            stageId: toStageId,
            sortOrder: Number.MAX_SAFE_INTEGER,
          },
          update(cache) {
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.ChangeStage });
      } catch (err) {
        toastMutationError(err, "Failed to update stage");
      }
    },
    [moveStageMutation, track],
  );

  const toggleFavorite = useCallback(
    async (issueId: string) => {
      const isFavorited = favoriteIssueIds.has(issueId);
      try {
        if (isFavorited) {
          await removeFavoriteMutation({
            variables: { type: "issue", referenceId: issueId },
          });
        } else {
          await addFavoriteMutation({
            variables: { type: "issue", referenceId: issueId },
          });
        }
        track({ context: AnalyticsContext.Kanban, event: isFavorited ? KanbanEvent.Unfavorite : KanbanEvent.Favorite });
        // The card shows no favorite state, so the toast is the only answer.
        toastManager.add({
          title: isFavorited ? "Removed from favorites" : "Added to favorites",
          type: "success",
        });
      } catch (err) {
        toastMutationError(err, "Failed to update favorite");
      }
    },
    [favoriteIssueIds, addFavoriteMutation, removeFavoriteMutation, track],
  );

  const moveToWorkflow = useCallback(
    async (issue: IssueRef, workflowId: string, stageId?: string) => {
      try {
        const result = await moveToWorkflowMutation({
          variables: { issueId: issue.issueId, workflowId, ...(stageId ? { stageId } : {}) },
          update(cache, { data }) {
            if (data?.updateIssue?.__typename !== "Issue") return;
            removeIssueFromStageCache(cache, issue.stageId, issue.issueId);
          },
        });
        const payload = result.data?.updateIssue;
        if (payload?.__typename !== "Issue") {
          throw new Error(resultMessage(payload) ?? "Move failed");
        }
        track({
          context: AnalyticsContext.Kanban,
          event: KanbanEvent.MoveWorkflow,
          data: { via: "menu", stagePicked: !!stageId },
        });
        // The card leaves this board, so the toast says where it went.
        toastManager.add({ title: "Moved to workflow", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to move to workflow");
      }
    },
    [moveToWorkflowMutation, track],
  );

  const updateAssignees = useCallback(
    async (issueId: string, entityIds: string[]) => {
      try {
        await updateAssigneesMutation({
          variables: {
            issueId,
            assignees: entityIds.map((id, index) => ({
              entityType: "user" as EntityType,
              entityId: id,
              isPrimary: index === 0,
            })),
          },
        });
        track({ context: AnalyticsContext.Kanban, event: KanbanEvent.UpdateAssignees, data: { count: entityIds.length } });
      } catch (err) {
        toastMutationError(err, "Failed to update assignees");
      }
    },
    [updateAssigneesMutation, track],
  );

  return {
    renameIssue,
    archiveIssue,
    unarchiveIssue,
    deleteIssue,
    changeStage,
    toggleFavorite,
    isIssueFavorited,
    moveToWorkflow,
    updateAssignees,
  };
}
